import cn from 'classnames';
import { useMemo } from 'react';

import { getTimeRangeDefaults } from '../../../utils';
import { Select } from '../../inputs/select';
import { SelectOption } from '../../inputs/select-option';

import type { ReactElement } from 'react';

import type { TimeSelectorProps } from './time-selector.types';

import './time-selector.scss';

/**
 * TimeSelector provides hour, minute, and second selection using Select dropdowns.
 *
 * This component is used within the DateTimePicker popup to allow users to select
 * specific time values. It shows different selectors based on the granularity prop
 * and maintains the current date while updating only the time components.
 *
 * ## Features
 *
 * - **Granular Control**: Shows appropriate selectors based on granularity
 * - **24-hour Format**: Uses 24-hour time format (0-23 for hours)
 * - **Current Value Preservation**: Maintains existing date when updating time
 * - **Accessibility**: Uses semantic Select components with proper labeling
 *
 * @param props - The props for the TimeSelector component
 * @returns A time selection panel with appropriate selectors
 */
const TimeSelector = (props: TimeSelectorProps): null | ReactElement => {
  const {
    className,
    disabled = false,
    granularity = 'minute',
    hourCycle = 12,
    onChange,
    rangeType = 'single',
    readOnly = false,
    value,
  } = props;

  // Calculate default time values based on range type and granularity
  const defaultTime = getTimeRangeDefaults(rangeType, granularity);

  // Extract current time values or use calculated defaults
  const currentHour = value?.getHours() ?? defaultTime.hour;
  const currentMinute = value?.getMinutes() ?? defaultTime.minute;
  const currentSecond = value?.getSeconds() ?? defaultTime.second;

  // Determine if we're in 12-hour format
  const is12HourFormat = hourCycle === 12;

  // Calculate display hour and AM/PM for 12-hour format
  const { displayHour, isAM } = useMemo(() => {
    if (!is12HourFormat) {
      return { displayHour: currentHour, isAM: true };
    }

    // Convert 24-hour to 12-hour format
    const hour12 = currentHour % 12 || 12;
    return {
      displayHour: hour12,
      isAM: currentHour < 12,
    };
  }, [currentHour, is12HourFormat]);

  // Generate options for each time component
  const hourOptions = useMemo(() => {
    if (is12HourFormat) {
      // 12-hour format: 01-12
      return Array.from({ length: 12 }, (_, i) => {
        const hour = i + 1;
        return {
          label: hour.toString().padStart(2, '0'),
          value: hour.toString(),
        };
      });
    } else {
      // 24-hour format: 00-23
      return Array.from({ length: 24 }, (_, i) => ({
        label: i.toString().padStart(2, '0'),
        value: i.toString(),
      }));
    }
  }, [is12HourFormat]);

  const minuteOptions = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        label: i.toString().padStart(2, '0'),
        value: i.toString(),
      })),
    [],
  );

  const secondOptions = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        label: i.toString().padStart(2, '0'),
        value: i.toString(),
      })),
    [],
  );

  /**
   * Updates the time component while preserving the date and other time components.
   */
  const updateTime = (hours: number, minutes: number, seconds: number): void => {
    // Use current date or today if no value exists
    const baseDate = value || new Date();
    const newDate = new Date(baseDate);

    // Set the new time values
    newDate.setHours(hours, minutes, seconds, baseDate.getMilliseconds());

    onChange?.(newDate);
  };

  /**
   * Handle hour change while preserving minutes and seconds.
   */
  const handleHourChange = (_event: any, newHour: null | string): void => {
    if (newHour !== null) {
      let hourValue = Number.parseInt(newHour, 10);

      // Convert 12-hour format to 24-hour format if needed
      if (is12HourFormat) {
        if (hourValue === 12) {
          hourValue = isAM ? 0 : 12;
        } else {
          hourValue = isAM ? hourValue : hourValue + 12;
        }
      }

      updateTime(hourValue, currentMinute, currentSecond);
    }
  };

  /**
   * Handle minute change while preserving hours and seconds.
   */
  const handleMinuteChange = (_event: any, newMinute: null | string): void => {
    if (newMinute !== null) {
      const minuteValue = Number.parseInt(newMinute, 10);
      updateTime(currentHour, minuteValue, currentSecond);
    }
  };

  /**
   * Handle second change while preserving hours and minutes.
   */
  const handleSecondChange = (_event: any, newSecond: null | string): void => {
    if (newSecond !== null) {
      const secondValue = Number.parseInt(newSecond, 10);
      updateTime(currentHour, currentMinute, secondValue);
    }
  };

  /**
   * Handle AM/PM change while preserving other time components.
   */
  const handleAmPmChange = (_event: any, newAmPm: null | string): void => {
    if (newAmPm !== null && is12HourFormat) {
      const newIsAM = newAmPm === 'AM';
      let newHour = currentHour;

      // Convert between AM and PM
      if (newIsAM && currentHour >= 12) {
        // Switching from PM to AM
        newHour = currentHour - 12;
      } else if (!newIsAM && currentHour < 12) {
        // Switching from AM to PM
        newHour = currentHour + 12;
      }

      updateTime(newHour, currentMinute, currentSecond);
    }
  };

  return (
    <div className={cn('rbui-time-selector', className)}>
      <span className="rbui-time-selector__title">Time</span>

      <div className="rbui-time-selector__selectors">
        {/* Hour Selector */}
        <Select
          className="rbui-time-selector__select"
          disabled={disabled}
          id="hour-select"
          onChange={handleHourChange}
          readOnly={readOnly}
          value={displayHour.toString()}
        >
          {hourOptions.map((option) => (
            <SelectOption key={option.value} value={option.value}>
              {option.label}
            </SelectOption>
          ))}
        </Select>

        {/* Minute Selector - shown for minute and second granularity */}
        {(granularity === 'minute' || granularity === 'second') && (
          <Select
            className="rbui-time-selector__select"
            disabled={disabled}
            id="minute-select"
            onChange={handleMinuteChange}
            readOnly={readOnly}
            value={currentMinute.toString()}
          >
            {minuteOptions.map((option) => (
              <SelectOption key={option.value} value={option.value}>
                {option.label}
              </SelectOption>
            ))}
          </Select>
        )}

        {/* Second Selector - shown only for second granularity */}
        {granularity === 'second' && (
          <Select
            className="rbui-time-selector__select"
            disabled={disabled}
            id="second-select"
            onChange={handleSecondChange}
            readOnly={readOnly}
            value={currentSecond.toString()}
          >
            {secondOptions.map((option) => (
              <SelectOption key={option.value} value={option.value}>
                {option.label}
              </SelectOption>
            ))}
          </Select>
        )}

        {/* AM/PM Selector - only shown in 12-hour format */}
        {is12HourFormat && (
          <Select
            className="rbui-time-selector__select"
            disabled={disabled}
            id="am-pm-select"
            onChange={handleAmPmChange}
            readOnly={readOnly}
            value={isAM ? 'AM' : 'PM'}
          >
            <SelectOption key="AM" value="AM">
              AM
            </SelectOption>
            <SelectOption key="PM" value="PM">
              PM
            </SelectOption>
          </Select>
        )}
      </div>
    </div>
  );
};

TimeSelector.displayName = 'TimeSelector';

export default TimeSelector;
